import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { VerdaAvailabilityHistory, VerdaAvailabilityShape } from '../types/models/Verda';
import type {
  ApiResponseVerdaAvailabilityHistory,
  ApiResponseVerdaAvailabilityShape,
} from '../types/api/ApiResponseVerda';
import { isAbortError, jsendFieldErrors, readJsendBody } from './gpuServerResponse';
import type { GpuJsendEnvelope } from './gpuServerResponse';

// Staff-only hooks over the "Availability history" section of docs/api-types/admin-verda.md: the series the
// Verda availability sampler records every 5 minutes. DATABASE reads (no Verda call, no rate limit), so a
// read runs when enabled, again whenever its query changes, and on `refetch` — never because `apiFetch`
// changed identity on an OIDC silent renew (read through a ref). While a new query loads, the previous
// result stays in `data` (the charts dim instead of flashing a spinner); `loading` says it is stale.

/** The window query: whole UTC days, both `YYYY-MM-DD`; `null` = the backend's default (the last 7 days). */
export type VerdaHistoryQuery = { from: string; to: string } | null;

/** The router's `fail` (no `message`): the backend doesn't have the routes deployed yet. */
const ROUTE_MISSING_MESSAGE = 'The availability history is not available on this backend yet.';

/** The message for a non-success body; a validation error is spelled out with its field messages. */
export function historyErrorMessage(body: GpuJsendEnvelope | null, fallback: string): string {
  const fieldMessages = Object.values(jsendFieldErrors(body));
  if (typeof body?.message === 'string' && body.message !== '') {
    return body.message === 'Request contains errors' && fieldMessages.length > 0
      ? `${body.message}: ${fieldMessages.join('; ')}`
      : body.message;
  }
  return body?.status === 'fail' ? ROUTE_MISSING_MESSAGE : fallback;
}

/** `GET /admin/verda/availability-history[?from=&to=]` (exported for tests). */
export function availabilityHistoryPath(query: VerdaHistoryQuery): string {
  return query === null
    ? '/admin/verda/availability-history'
    : `/admin/verda/availability-history?${new URLSearchParams(query).toString()}`;
}

/** `GET /admin/verda/availability-history/shape?instance_type=…[&from=&to=]` (exported for tests). */
export function availabilityShapePath(instanceType: string, query: VerdaHistoryQuery): string {
  const params = new URLSearchParams({ instance_type: instanceType, ...(query ?? {}) });
  return `/admin/verda/availability-history/shape?${params.toString()}`;
}

interface HistoryReadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function useHistoryRead<R extends GpuJsendEnvelope, T>(
  path: string | null,
  pick: (body: R) => T | null,
  what: string,
  enabled: boolean,
): HistoryReadState<T> {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled && path !== null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedPathRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (path === null) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRef.current(path, { signal: controller.signal });
      const body = await readJsendBody<R>(response);
      if (controller.signal.aborted) {
        return;
      }
      const picked = body?.status === 'success' ? pick(body) : null;
      if (picked !== null) {
        setData(picked);
        return;
      }
      setError(historyErrorMessage(body, `Could not load the ${what}.`));
    } catch (err) {
      if (isAbortError(err, controller.signal)) {
        return;
      }
      setError(err instanceof Error ? err.message : `An error occurred while loading the ${what}.`);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [path, pick, what]);

  useEffect(() => {
    if (!enabled || !auth.isAuthenticated || path === null || fetchedPathRef.current === path) {
      return;
    }
    fetchedPathRef.current = path;
    void load();
  }, [enabled, auth.isAuthenticated, path, load]);

  // Forgetting the fetched path makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      fetchedPathRef.current = null;
    },
    [],
  );

  return { data, loading, error, refetch: load };
}

const pickHistory = (body: ApiResponseVerdaAvailabilityHistory): VerdaAvailabilityHistory | null => {
  const d = body.data;
  return d?.window && d.coverage && Array.isArray(d.locations) && Array.isArray(d.instance_types)
    ? { window: d.window, coverage: d.coverage, locations: d.locations, instance_types: d.instance_types }
    : null;
};

const pickShape = (body: ApiResponseVerdaAvailabilityShape): VerdaAvailabilityShape | null => {
  const d = body.data;
  return d?.window && d.instance_type && Array.isArray(d.locations) && d.anywhere
    ? { window: d.window, instance_type: d.instance_type, locations: d.locations, anywhere: d.anywhere }
    : null;
};

/** The overview: coverage per UTC day + every instance type sampled in the window, with its counts. */
export const useAdminVerdaAvailabilityHistory = (query: VerdaHistoryQuery, enabled: boolean = true) => {
  const { data, ...rest } = useHistoryRead(
    availabilityHistoryPath(query),
    pickHistory,
    'Verda availability history',
    enabled,
  );
  return { history: data, ...rest };
};

/** One instance type's slot series (per location + anywhere); `instanceType === null` = nothing selected. */
export const useAdminVerdaAvailabilityShape = (
  instanceType: string | null,
  query: VerdaHistoryQuery,
  enabled: boolean = true,
) => {
  const { data, ...rest } = useHistoryRead(
    instanceType === null ? null : availabilityShapePath(instanceType, query),
    pickShape,
    'availability of this instance type',
    enabled,
  );
  // A previous type's series must never be drawn under the newly selected one's name.
  const shape = data !== null && data.instance_type.instance_type === instanceType ? data : null;
  return { shape, ...rest };
};
